/**
 * Правые панели (чат-агент и карточка сущности) не должны перекрывать
 * страницу, когда на экране есть место.
 *
 * Обе панели — `position: fixed` у правого края, то есть вне потока: сами
 * по себе они всегда лежат ПОВЕРХ контента. Хук считает, сколько места
 * они занимают, и резервирует его через `padding-right` на `<body>`.
 * Всё, что в потоке (sticky TopBar, левый сайдбар, лента нарративов),
 * сжимается и уезжает влево — панель встаёт рядом, а не сверху.
 *
 * Резервируем только если после этого контенту остаётся не меньше
 * `MIN_CONTENT`: на узком экране раздвигать некуда, и overlay остаётся
 * правильным поведением (как было). Мобайл не трогаем вообще — там
 * панели по определению полноэкранные.
 *
 * Почему `<body>`, а не контейнер страницы: панели глобальные (чат
 * смонтирован в App, карточка — на каждой странице репо), а страниц
 * несколько (нарратив / план / отчёты / сравнение). Одно место вместо
 * четырёх одинаковых обвязок.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';
import { useChatStore } from '../stores/chat-store';
import { useUiStore } from '../stores/ui-store';
import { DESKTOP_QUERY } from '../lib/breakpoints';

/** Ширина чат-панели — синхронно с GlobalChatDrawer (`width: 400`). */
export const CHAT_PANEL_WIDTH = 400;

/** Ширина карточки сущности — Mantine Drawer `size="lg"`. */
export const ENTITY_CARD_WIDTH = 620;

/**
 * Сколько ширины оставляем контенту, чтобы док имел смысл. 960px — лента
 * нарративов (max-width 920) плюс воздух; левый сайдбар при этом уже
 * учтён в вёрстке страницы и ужимается вместе с ней.
 */
const MIN_CONTENT = 960;

export function useRightDock(): void {
    const chatOpen   = useChatStore(s => s.open);
    const chatCtx    = useChatStore(s => s.ctx);
    const cardEntity = useUiStore(s => s.cardEntity);
    const isDesktop  = useMediaQuery(DESKTOP_QUERY, true);
    const location   = useLocation();
    const [viewport, setViewport] = useState(() => window.innerWidth);

    useEffect(() => {
        const onResize = (): void => setViewport(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Чат живёт только на страницах репо (см. GlobalChatDrawer) — на
    // главной и в админке резервировать нечего.
    const onRepoPage = location.pathname.startsWith('/r/');

    useEffect(() => {
        const wanted =
            (onRepoPage && chatCtx && chatOpen ? CHAT_PANEL_WIDTH : 0)
            + (cardEntity ? ENTITY_CARD_WIDTH : 0);
        const fits = isDesktop && wanted > 0 && viewport - wanted >= MIN_CONTENT;
        document.body.style.paddingRight = fits ? `${wanted}px` : '';
        return () => { document.body.style.paddingRight = ''; };
    }, [onRepoPage, chatCtx, chatOpen, cardEntity, isDesktop, viewport]);
}
